import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useConfig } from "../hooks/useConfig";
import API, { formatError } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { LogOut, Search, X, GraduationCap, Award, Users, Building2, ThumbsUp, ThumbsDown, CalendarCheck, FileText, Filter, Moon, Sun, Download, LayoutGrid, List, Briefcase, Plus } from "lucide-react";
import KanbanBoard from "../components/KanbanBoard";
import ApplicationDeepDive from "../components/ApplicationDeepDive";
import TenantUsageBar from "../components/TenantUsageBar";

function computeFitScore(student, companyData) {
  const skills = (student.extracted_skills || []).map(s => s.toLowerCase());
  const classifiedRole = (student.classified_role || "").toLowerCase();
  const cgpa = parseFloat(student.cgpa) || 0;
  const hasResume = !!(student.has_resume || student.ai_summary);

  const skillScore = companyData
    ? Math.min(
        ((companyData.keywords || []).filter(kw =>
          skills.some(s => s === kw.toLowerCase() || s.includes(kw.toLowerCase()) || kw.toLowerCase().includes(s))
        ).length / 4) * 65,
        65
      )
    : Math.min((skills.length / 4) * 65, 65);

  const roleMatches = companyData
    ? (companyData.roleKeys || []).some(rk => classifiedRole.includes(rk))
    : false;

  const cgpaScore = Math.min((cgpa / 10) * 10, 10);
  const expScore = hasResume ? 15 : 0;
  const roleBonus = roleMatches ? 10 : 0;

  return Math.round(Math.min(skillScore + cgpaScore + expScore + roleBonus, 99));
}

function getStatusStyle(status, isDark) {
  if (isDark) {
    const map = {
      Pending: { background: 'rgba(234,179,8,0.12)', color: '#fbbf24', border: '1px solid rgba(234,179,8,0.25)' },
      Interested: { background: 'rgba(5,150,105,0.12)', color: '#34d399', border: '1px solid rgba(5,150,105,0.25)' },
      Shortlisted: { background: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)' },
      "Interview Scheduled": { background: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)' },
      "Offer Received": { background: 'rgba(5,150,105,0.12)', color: '#34d399', border: '1px solid rgba(5,150,105,0.25)' },
      Rejected: { background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' },
    };
    return map[status] || { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.1)' };
  }
  const map = {
    Pending: { background: 'rgba(234,179,8,0.1)', color: '#d97706', border: '1px solid rgba(234,179,8,0.25)' },
    Interested: { background: 'rgba(5,150,105,0.1)', color: '#059669', border: '1px solid rgba(5,150,105,0.25)' },
    Shortlisted: { background: 'rgba(59,130,246,0.1)', color: '#2563eb', border: '1px solid rgba(59,130,246,0.25)' },
    "Interview Scheduled": { background: 'rgba(59,130,246,0.1)', color: '#2563eb', border: '1px solid rgba(59,130,246,0.25)' },
    "Offer Received": { background: 'rgba(5,150,105,0.1)', color: '#059669', border: '1px solid rgba(5,150,105,0.25)' },
    Rejected: { background: 'rgba(239,68,68,0.1)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.25)' },
  };
  return map[status] || { background: 'rgba(0,0,0,0.04)', color: '#64748b', border: '1px solid rgba(0,0,0,0.1)' };
}

function CandidateCard({ student, fitScore, onAction, onReconsider, onViewProfile, actionLoading, activeAction, isDark, t }) {

  return (
    <Card className="hover:-translate-y-0.5 transition-all duration-200 glass-card" style={t.glassCard} data-testid={`candidate-card-${student.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm" style={{ background: t.accentLight, color: t.accent }}>
              {(student.name || "?")[0].toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold text-sm" style={{ color: t.textPrimary }}>{student.name}</h3>
              <p className="text-xs" style={{ color: t.textMuted }}>{student.college?.replace("BITS Pilani - ", "") || "—"}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold" style={{ color: t.accent }}>{fitScore}%</div>
            <div className="text-xs" style={{ color: t.textMuted }}>fit score</div>
          </div>
        </div>

        {student.classified_role && (
          <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mb-3" style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', color: t.textSecondary, border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)' }}>
            {student.classified_role}
          </span>
        )}

        <div className="grid grid-cols-2 gap-2 mb-3 text-xs" style={{ color: t.textSecondary }}>
          <div className="flex items-center gap-1.5">
            <Award size={11} style={{ color: t.accent }} />
            <span>CGPA: {student.cgpa || "—"}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <GraduationCap size={11} style={{ color: t.accent }} />
            <span>{student.branch?.slice(0, 15) || "—"}</span>
          </div>
        </div>

        {student.extracted_skills?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {student.extracted_skills.slice(0, 3).map(s => (
              <span key={s} className="px-2 py-0.5 text-xs rounded-full border" style={{ background: isDark ? t.accentLight : t.accent, color: isDark ? '#34d399' : '#ffffff', borderColor: t.accentBorder }}>{s}</span>
            ))}
            {student.extracted_skills.length > 3 && (
              <span className="px-2 py-0.5 text-xs rounded-full" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', color: t.textMuted }}>+{student.extracted_skills.length - 3}</span>
            )}
          </div>
        )}
        {(() => {
          const verifiedCount = (student.verified_skills || []).filter(v => v.status === "verified").length;
          return verifiedCount > 0 ? (
            <div className="mb-3">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)' }}>
                ✓ {verifiedCount} skill{verifiedCount !== 1 ? "s" : ""} verified
              </span>
            </div>
          ) : null;
        })()}

        {activeAction ? (
          <div className="flex items-center justify-between gap-2 mb-2">
            <div>
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', color: t.textSecondary, border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)' }}>
                {activeAction.status} ✓
              </span>
              {activeAction.actionedAt && (
                <p className="text-xs mt-1 ml-1" style={{ color: t.textMuted }}>
                  {new Date(activeAction.actionedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </p>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7 px-3 shrink-0"
              style={{ borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', background: 'transparent', color: t.textSecondary }}
              onClick={() => onReconsider(student.user_id)}
              disabled={actionLoading === student.user_id}
              data-testid={`reconsider-btn-${student.id}`}
            >
              Reconsider
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-1.5 mb-2">
            {/* Interested = GREEN */}
            <Button
              size="sm"
              className="text-xs gap-1 h-8 px-2 font-semibold"
              style={{ background: 'rgba(5,150,105,0.15)', color: '#059669', border: '1px solid rgba(5,150,105,0.3)' }}
              onClick={() => onAction(student.user_id, "Interested")}
              disabled={actionLoading === student.user_id}
              data-testid={`interested-btn-${student.id}`}
            >
              <ThumbsUp size={11} /> Yes
            </Button>
            {/* Interview Scheduled = BLUE */}
            <Button
              size="sm"
              className="text-xs gap-1 h-8 px-2 font-semibold"
              style={{ background: 'rgba(59,130,246,0.15)', color: '#2563eb', border: '1px solid rgba(59,130,246,0.3)' }}
              onClick={() => onAction(student.user_id, "Interview Scheduled")}
              disabled={actionLoading === student.user_id}
              data-testid={`interview-btn-${student.id}`}
            >
              <CalendarCheck size={11} /> Interview
            </Button>
            {/* Pass/Rejected = RED */}
            <Button
              size="sm"
              className="text-xs gap-1 h-8 px-2 font-semibold"
              style={{ background: 'rgba(239,68,68,0.15)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.3)' }}
              onClick={() => onAction(student.user_id, "Rejected")}
              disabled={actionLoading === student.user_id}
              data-testid={`reject-btn-${student.id}`}
            >
              <ThumbsDown size={11} /> Pass
            </Button>
          </div>
        )}

        <Button
          size="sm"
          variant="outline"
          className="w-full text-xs gap-1.5"
          style={{ borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', background: 'transparent', color: t.textSecondary }}
          onClick={() => onViewProfile(student)}
          data-testid={`view-profile-btn-${student.id}`}
        >
          View Full Profile
        </Button>
      </CardContent>
    </Card>
  );
}

function PipelineCard({ item, onViewProfile, isDark, t }) {
  const student = item.student;
  if (!student) return null;

  return (
    <Card style={t.glassCard} data-testid={`pipeline-card-${item.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm" style={{ background: t.accentLight, color: t.accent }}>
              {(student.name || "?")[0].toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-sm" style={{ color: t.textPrimary }}>{student.name}</p>
              <p className="text-xs" style={{ color: t.textMuted }}>{student.classified_role || student.college || "—"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 rounded-full text-xs font-medium" style={getStatusStyle(item.status, isDark)}>
              {item.status}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="text-xs h-7 px-2"
              style={{ color: t.textSecondary }}
              onClick={() => onViewProfile(student)}
            >
              View
            </Button>
          </div>
        </div>
        {student.extracted_skills?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2 ml-12">
            {student.extracted_skills.slice(0, 4).map(s => (
              <span key={s} className="px-1.5 py-0.5 text-xs rounded" style={{ background: isDark ? t.accentLight : t.accent, color: isDark ? '#34d399' : '#ffffff' }}>{s}</span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function RecruiterDashboard() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { config } = useConfig();
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [candidateActions, setCandidateActions] = useState({});

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [resumeUrl, setResumeUrl] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);

  const [deepDiveApp, setDeepDiveApp] = useState(null);
  const [viewMode, setViewMode] = useState("kanban");
  const [tenantUsage, setTenantUsage] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [jobTitle, setJobTitle] = useState("");
  const [jobJD, setJobJD] = useState("");
  const [jobSkills, setJobSkills] = useState("");
  const [jobThreshold, setJobThreshold] = useState("60");
  const [jobPosting, setJobPosting] = useState(false);
  const [jobMsg, setJobMsg] = useState("");

  const [filterRole, setFilterRole] = useState("");
  const [filterSkills, setFilterSkills] = useState("");
  const [filterCgpaMin, setFilterCgpaMin] = useState("");
  const [filterCgpaMax, setFilterCgpaMax] = useState("");

  const companyName = user?.company_name || "Your Company";

  const myCompanyData = useMemo(() => {
    if (!config?.companies || !companyName) return null;
    const name = companyName.toLowerCase();
    return config.companies.find(c => c.name.toLowerCase() === name) || null;
  }, [config, companyName]);

  // Theme-aware styles
  const pageBg = isDark ? '#0a0a0f' : '#f8f9fa';
  const headerBg = isDark ? 'rgba(10,10,15,0.9)' : 'rgba(255,255,255,0.95)';
  const headerBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const textPrimary = isDark ? '#ffffff' : '#0a0a0f';
  const textSecondary = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
  const textMuted = isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8';
  const cardBg = isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)';
  const cardShadow = isDark ? 'none' : '0 1px 8px rgba(0,0,0,0.05)';
  const glassCard = { background: cardBg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: cardBorder, boxShadow: cardShadow };
  const inputStyle = isDark
    ? { background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)' }
    : { background: 'rgba(0,0,0,0.04)', borderColor: 'rgba(0,0,0,0.1)' };
  const accent = '#059669';
  const accentDark = '#047857';
  const accentLight = isDark ? 'rgba(5,150,105,0.15)' : 'rgba(5,150,105,0.08)';
  const accentBorder = 'rgba(5,150,105,0.25)';

  const t = { glassCard, inputStyle, accent, accentDark, accentLight, accentBorder, textPrimary, textSecondary, textMuted, bg: pageBg, cardBg, border: cardBorder };

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterRole) params.role = filterRole;
      if (filterSkills) params.skills = filterSkills;
      if (filterCgpaMin) params.cgpa_min = parseFloat(filterCgpaMin);
      if (filterCgpaMax) params.cgpa_max = parseFloat(filterCgpaMax);
      const res = await API.get("/api/recruiter/students", { params });
      setStudents(res.data);
    } catch (e) {
      console.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [filterRole, filterSkills, filterCgpaMin, filterCgpaMax]);

  const fetchPipeline = useCallback(async () => {
    try {
      const res = await API.get("/api/recruiter/pipeline");
      setPipeline(res.data);
      const actions = {};
      res.data.forEach(item => {
        if (item.student?.user_id && item.status !== "Pending") {
          actions[item.student.user_id] = {
            status: item.status,
            actionedAt: item.updated_at || item.created_at || null,
          };
        }
      });
      setCandidateActions(prev => ({ ...prev, ...actions }));
    } catch (e) {
      console.error(formatError(e));
    }
  }, []);

  useEffect(() => {
    window.history.replaceState(null, "", "/");
    window.history.pushState(null, "", "/recruiter/dashboard");
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await API.get("/api/jobs");
      setJobs(res.data);
    } catch (e) {
      console.error(formatError(e));
    }
  }, []);

  const handlePostJob = async (e) => {
    e.preventDefault();
    if (!jobTitle.trim() || !jobJD.trim()) return;
    setJobPosting(true);
    setJobMsg("");
    try {
      const mandatory_skills = jobSkills.split(",").map(s => s.trim()).filter(Boolean)
        .map(s => ({ skill: s, weight: 5 }));
      await API.post("/api/jobs", {
        title: jobTitle.trim(),
        jd_text: jobJD.trim(),
        mandatory_skills,
        screening_threshold: parseInt(jobThreshold) || 60,
      });
      setJobMsg("Job posted successfully!");
      setJobTitle(""); setJobJD(""); setJobSkills(""); setJobThreshold("60");
      fetchJobs();
    } catch (e) {
      setJobMsg(formatError(e));
    } finally {
      setJobPosting(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchPipeline();
    fetchJobs();
    API.get("/api/recruiter/tenant-usage").then(r => setTenantUsage(r.data)).catch(() => {});
  }, []);

  const handleSearch = (e) => { e.preventDefault(); fetchStudents(); };

  const clearFilters = () => {
    setFilterRole(""); setFilterSkills(""); setFilterCgpaMin(""); setFilterCgpaMax("");
  };

  const handleAction = async (studentId, action) => {
    setActionLoading(studentId);
    try {
      await API.post("/api/recruiter/mark-candidate", { student_id: studentId, action });
      setCandidateActions(prev => ({
        ...prev,
        [studentId]: { status: action, actionedAt: new Date().toISOString() },
      }));
      await fetchPipeline();
    } catch (e) {
      console.error(formatError(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReconsider = async (studentId) => {
    setActionLoading(studentId);
    try {
      await API.post("/api/recruiter/mark-candidate", { student_id: studentId, action: "Pending" });
      setCandidateActions(prev => {
        const next = { ...prev };
        delete next[studentId];
        return next;
      });
      await fetchPipeline();
    } catch (e) {
      console.error(formatError(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewProfile = async (student) => {
    setSelectedStudent(student);
    setResumeUrl(null);
    setResumeLoading(true);
    try {
      const res = await API.get(`/api/recruiter/students/${student.user_id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setResumeUrl(url);
    } catch (_) {
      setResumeUrl(null);
    } finally {
      setResumeLoading(false);
    }
  };

  const handleCloseModal = useCallback(() => {
    setSelectedStudent(null);
    if (resumeUrl) { URL.revokeObjectURL(resumeUrl); setResumeUrl(null); }
  }, [resumeUrl]);

  const KANBAN_STAGES = config.pipeline_stages;

  const handleStageChange = async (applicationId, newStage) => {
    try {
      await API.put(`/api/recruiter/applications/${applicationId}/status`, { status: newStage });
      setPipeline(prev => prev.map(a => a.id === applicationId ? { ...a, status: newStage } : a));
    } catch (e) {
      console.error(formatError(e));
    }
  };

  const handleSaveNote = async (note) => {
    if (!deepDiveApp) return;
    try {
      await API.patch(`/api/jobs/${deepDiveApp.job_id}/applications/${deepDiveApp.id}/notes`, { notes: note });
      setDeepDiveApp(prev => prev ? { ...prev, notes: note } : prev);
    } catch (e) {
      console.error(formatError(e));
    }
  };

  const handleExport = async () => {
    try {
      const res = await API.get("/api/recruiter/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "pipeline.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(formatError(e));
    }
  };

  const handleLogout = () => { logout(); window.location.replace("/"); };

  const hasFilters = filterRole || filterSkills || filterCgpaMin || filterCgpaMax;

  return (
    <div className="min-h-screen" style={{ background: pageBg }}>
      {/* Header */}
      <header className="sticky top-0 z-10" style={{ background: headerBg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: `1px solid ${headerBorder}` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})` }}>
              <span className="text-white font-bold text-xs">W</span>
            </div>
            <div>
              <span className="font-bold block leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>
                {companyName}
              </span>
              <span className="text-xs" style={{ color: textMuted }}>Recruiter Portal</span>
            </div>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium border hidden sm:flex" style={{ background: isDark ? accentLight : accent, borderColor: accentBorder, color: isDark ? '#34d399' : '#ffffff' }}>
              {user?.name || "Recruiter"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <TenantUsageBar usage={tenantUsage} isDark={isDark} />
            <button
              onClick={e => toggleTheme(e)}
              aria-label="Toggle theme"
              style={{ width: '30px', height: '30px', borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: textSecondary }}
            >
              {isDark ? <Sun size={13} /> : <Moon size={13} />}
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-colors"
              style={{ color: textMuted, background: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              data-testid="recruiter-logout-btn"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { icon: <Users size={16} />, label: "Total Candidates", value: students.length },
            { icon: <Building2 size={16} />, label: "Roles Available", value: [...new Set(students.map(s => s.classified_role).filter(Boolean))].length },
            { icon: <GraduationCap size={16} />, label: "Campuses", value: [...new Set(students.map(s => s.college).filter(Boolean))].length },
            { icon: <Award size={16} />, label: "Avg. CGPA", value: students.length > 0 ? (students.reduce((a, s) => a + (s.cgpa || 0), 0) / students.length).toFixed(1) : "—" },
          ].map((stat, i) => (
            <div key={i} className="rounded-xl p-4 glass-card" style={glassCard}>
              <div className="flex items-center gap-2 mb-2" style={{ color: accent }}>{stat.icon}<span className="text-xs" style={{ color: textSecondary }}>{stat.label}</span></div>
              <div className="text-2xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>{stat.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="pool">
          <TabsList className="mb-6" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)' }}>
            <TabsTrigger value="pool" className="gap-2" data-testid="tab-talent-pool">
              <Users size={14} /> Talent Pool ({students.length})
            </TabsTrigger>
            <TabsTrigger value="pipeline" className="gap-2" data-testid="tab-pipeline">
              <CalendarCheck size={14} /> My Pipeline ({pipeline.length})
            </TabsTrigger>
            <TabsTrigger value="jobs" className="gap-2" data-testid="tab-jobs">
              <Briefcase size={14} /> Job Postings ({jobs.length})
            </TabsTrigger>
          </TabsList>

          {/* Talent Pool Tab */}
          <TabsContent value="pool">
            <form onSubmit={handleSearch} className="rounded-xl p-4 mb-6 glass" style={glassCard}>
              <div className="flex items-center gap-2 mb-3">
                <Filter size={14} style={{ color: textMuted }} />
                <span className="text-sm font-medium" style={{ color: textSecondary }}>Filter Candidates</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: textMuted }}>Role</Label>
                  <Input value={filterRole} onChange={e => setFilterRole(e.target.value)} placeholder="Software Engineer..." className="text-sm" style={{ ...inputStyle, color: textPrimary }} data-testid="filter-role" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: textMuted }}>Skills (comma-separated)</Label>
                  <Input value={filterSkills} onChange={e => setFilterSkills(e.target.value)} placeholder="Python, React..." className="text-sm" style={{ ...inputStyle, color: textPrimary }} data-testid="filter-skills" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: textMuted }}>CGPA Min</Label>
                  <Input type="number" step="0.1" min="0" max="10" value={filterCgpaMin} onChange={e => setFilterCgpaMin(e.target.value)} placeholder="7.0" className="text-sm" style={{ ...inputStyle, color: textPrimary }} data-testid="filter-cgpa-min" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: textMuted }}>CGPA Max</Label>
                  <Input type="number" step="0.1" min="0" max="10" value={filterCgpaMax} onChange={e => setFilterCgpaMax(e.target.value)} placeholder="10.0" className="text-sm" style={{ ...inputStyle, color: textPrimary }} data-testid="filter-cgpa-max" />
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button type="submit" size="sm" className="font-semibold gap-2" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }} data-testid="search-candidates-btn">
                  <Search size={14} /> Search
                </Button>
                {hasFilters && (
                  <Button type="button" size="sm" variant="outline" onClick={clearFilters} className="gap-2" style={{ borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', background: 'transparent', color: textSecondary }} data-testid="clear-filters-btn">
                    <X size={14} /> Clear
                  </Button>
                )}
              </div>
            </form>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent, borderTopColor: 'transparent' }} />
              </div>
            ) : students.length === 0 ? (
              <div className="text-center py-20">
                <Users size={40} className="mx-auto mb-3" style={{ color: textMuted }} />
                <p style={{ color: textMuted }}>No candidates match your filters.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="candidates-grid">
                {students.map(student => (
                  <CandidateCard
                    key={student.id}
                    student={student}
                    fitScore={computeFitScore(student, myCompanyData)}
                    onAction={handleAction}
                    onReconsider={handleReconsider}
                    onViewProfile={handleViewProfile}
                    actionLoading={actionLoading}
                    activeAction={candidateActions[student.user_id]}
                    isDark={isDark}
                    t={t}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Pipeline Tab */}
          <TabsContent value="pipeline">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>
                  {companyName} Pipeline
                </h2>
                <span className="px-2.5 py-1 rounded-full text-xs font-medium border" style={{ background: accentLight, borderColor: accentBorder, color: accent }}>
                  {pipeline.length} candidate{pipeline.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)' }}>
                  <button
                    onClick={() => setViewMode("kanban")}
                    className="px-3 py-1.5 flex items-center gap-1.5 text-xs transition-colors"
                    style={{ background: viewMode === "kanban" ? accent : 'transparent', color: viewMode === "kanban" ? '#fff' : textSecondary }}
                    data-testid="view-kanban-btn"
                  >
                    <LayoutGrid size={13} /> Board
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className="px-3 py-1.5 flex items-center gap-1.5 text-xs transition-colors"
                    style={{ background: viewMode === "list" ? accent : 'transparent', color: viewMode === "list" ? '#fff' : textSecondary }}
                    data-testid="view-list-btn"
                  >
                    <List size={13} /> List
                  </button>
                </div>
                {pipeline.length > 0 && (
                  <Button size="sm" variant="outline" onClick={handleExport} className="gap-1.5 text-xs"
                    style={{ borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', background: 'transparent', color: textSecondary }}
                    data-testid="export-csv-btn">
                    <Download size={13} /> Export CSV
                  </Button>
                )}
              </div>
            </div>

            {pipeline.length === 0 ? (
              <div className="text-center py-20">
                <CalendarCheck size={40} className="mx-auto mb-3" style={{ color: textMuted }} />
                <p className="mb-2" style={{ color: textMuted }}>No candidates in your pipeline yet.</p>
                <p className="text-sm" style={{ color: textMuted }}>Mark candidates as Interested or Schedule Interviews in the Talent Pool tab.</p>
              </div>
            ) : viewMode === "kanban" ? (
              <KanbanBoard
                stages={KANBAN_STAGES}
                applicationsByStage={Object.fromEntries(KANBAN_STAGES.map(s => [s, pipeline.filter(a => a.status === s)]))}
                onStageChange={handleStageChange}
                onCardClick={setDeepDiveApp}
                isDark={isDark}
                t={t}
              />
            ) : (
              <div className="space-y-3" data-testid="pipeline-list">
                {pipeline.map(item => (
                  <PipelineCard key={item.id} item={item} onViewProfile={handleViewProfile} isDark={isDark} t={t} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Job Postings Tab */}
          <TabsContent value="jobs">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Post a Job form */}
              <Card style={glassCard} data-testid="post-job-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2" style={{ color: textPrimary }}>
                    <Plus size={16} style={{ color: accent }} /> Post a New Job
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handlePostJob} className="flex flex-col gap-4">
                    <div>
                      <Label className="text-xs mb-1 block" style={{ color: textMuted }}>Job Title *</Label>
                      <Input value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="e.g. Software Engineer - Backend" className="text-sm" style={{ ...inputStyle, color: textPrimary }} data-testid="job-title-input" required />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block" style={{ color: textMuted }}>Job Description *</Label>
                      <textarea
                        value={jobJD}
                        onChange={e => setJobJD(e.target.value)}
                        placeholder="Describe the role, responsibilities, and requirements..."
                        rows={5}
                        required
                        data-testid="job-jd-input"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13, lineHeight: 1.6, resize: 'vertical', outline: 'none', fontFamily: 'inherit', ...inputStyle, color: textPrimary, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}` }}
                      />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block" style={{ color: textMuted }}>Required Skills <span style={{ color: textMuted }}>(comma-separated)</span></Label>
                      <Input value={jobSkills} onChange={e => setJobSkills(e.target.value)} placeholder="Python, React, SQL..." className="text-sm" style={{ ...inputStyle, color: textPrimary }} data-testid="job-skills-input" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block" style={{ color: textMuted }}>Screening Threshold <span style={{ color: textMuted }}>(merit score min, 0–100)</span></Label>
                      <Input type="number" min="0" max="100" value={jobThreshold} onChange={e => setJobThreshold(e.target.value)} className="text-sm" style={{ ...inputStyle, color: textPrimary }} data-testid="job-threshold-input" />
                    </div>
                    {jobMsg && (
                      <p className="text-sm px-3 py-2 rounded-lg" style={jobMsg.includes("success") ? { background: 'rgba(5,150,105,0.1)', color: isDark ? '#34d399' : '#059669', border: '1px solid rgba(5,150,105,0.2)' } : { background: 'rgba(239,68,68,0.1)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="job-msg">
                        {jobMsg}
                      </p>
                    )}
                    <Button type="submit" disabled={jobPosting} className="gap-2 font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }} data-testid="post-job-btn">
                      {jobPosting ? "Posting..." : <><Plus size={14} /> Post Job</>}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Existing jobs list */}
              <div className="flex flex-col gap-3">
                <h3 className="text-sm font-semibold" style={{ color: textSecondary }}>Your Active Postings</h3>
                {jobs.length === 0 ? (
                  <div className="rounded-xl p-8 text-center" style={glassCard}>
                    <Briefcase size={28} className="mx-auto mb-2" style={{ color: textMuted }} />
                    <p className="text-sm" style={{ color: textMuted }}>No jobs posted yet. Use the form to create your first posting.</p>
                  </div>
                ) : jobs.map(job => (
                  <div key={job.id} className="rounded-xl p-4" style={glassCard} data-testid={`job-card-${job.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="font-semibold text-sm" style={{ color: textPrimary }}>{job.title}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full shrink-0" style={{ background: job.status === 'active' ? 'rgba(5,150,105,0.12)' : 'rgba(100,116,139,0.12)', color: job.status === 'active' ? '#059669' : '#64748b' }}>{job.status || 'active'}</span>
                    </div>
                    <p className="text-xs mb-2 line-clamp-2" style={{ color: textSecondary }}>{job.jd_text}</p>
                    {(job.mandatory_skills || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {job.mandatory_skills.slice(0, 4).map(s => (
                          <span key={s.skill} className="text-xs px-2 py-0.5 rounded-full" style={{ background: accentLight, color: accent, border: `1px solid ${accentBorder}` }}>{s.skill}</span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs mt-2" style={{ color: textMuted }}>
                      Threshold: {job.screening_threshold ?? 60}% · Posted {job.created_at ? new Date(job.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Application Deep Dive Modal */}
      <ApplicationDeepDive
        application={deepDiveApp}
        isOpen={!!deepDiveApp}
        onClose={() => setDeepDiveApp(null)}
        onSaveNote={handleSaveNote}
        isDark={isDark}
        t={t}
      />

      {/* Student Profile Modal */}
      <Dialog open={!!selectedStudent} onOpenChange={handleCloseModal}>
        <DialogContent
          className="max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          style={{ background: isDark ? 'rgba(15,15,22,0.97)' : '#ffffff', backdropFilter: 'blur(30px)', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)' }}
          data-testid="student-profile-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm" style={{ background: accentLight, color: accent }}>
                {(selectedStudent?.name || "?")[0].toUpperCase()}
              </div>
              {selectedStudent?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                {[
                  { label: "College", value: selectedStudent.college },
                  { label: "Branch", value: selectedStudent.branch },
                  { label: "CGPA", value: selectedStudent.cgpa ? `${selectedStudent.cgpa} / 10` : "—" },
                  { label: "Graduation", value: selectedStudent.graduation_year || "—" },
                ].map(({ label, value }) => (
                  <div key={label} className="p-3 rounded-lg" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)' }}>
                    <p className="text-xs mb-1" style={{ color: textMuted }}>{label}</p>
                    <p className="font-medium text-sm truncate" style={{ color: textPrimary }}>{value}</p>
                  </div>
                ))}
              </div>

              {selectedStudent.classified_role && (
                <div>
                  <p className="text-xs mb-2 font-medium uppercase tracking-wide" style={{ color: textMuted }}>Best-fit Role</p>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }}>{selectedStudent.classified_role}</span>
                </div>
              )}

              {selectedStudent.extracted_skills?.length > 0 && (
                <div>
                  <p className="text-xs mb-2 font-medium uppercase tracking-wide" style={{ color: textMuted }}>Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedStudent.extracted_skills.map(s => {
                      const vs = (selectedStudent.verified_skills || []).find(v => v.skill_name?.toLowerCase() === s.toLowerCase());
                      const statusIcon = vs?.status === "verified" ? { sym: "✓", col: "#10b981" } : vs?.status === "partial" ? { sym: "~", col: "#f59e0b" } : null;
                      return (
                        <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border"
                          style={{ background: accentLight, borderColor: accentBorder, color: accent }}>
                          {s}
                          {statusIcon && (
                            <span className="font-bold text-[10px]" style={{ color: statusIcon.col }}>{statusIcon.sym}</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                  {(selectedStudent.verified_skills || []).filter(v => v.status === "verified").length > 0 && (
                    <p className="text-xs mt-2" style={{ color: "#10b981" }}>
                      ✓ {(selectedStudent.verified_skills || []).filter(v => v.status === "verified").length} skill{(selectedStudent.verified_skills || []).filter(v => v.status === "verified").length !== 1 ? "s" : ""} verified via quiz
                    </p>
                  )}
                </div>
              )}

              {selectedStudent.ai_summary && (
                <div className="p-3 rounded-lg" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)' }}>
                  <p className="text-xs font-medium mb-1" style={{ color: textMuted }}>AI Summary</p>
                  <p className="text-sm" style={{ color: textSecondary }}>{selectedStudent.ai_summary}</p>
                </div>
              )}

              {selectedStudent.preferred_role_type && (
                <div>
                  <p className="text-xs mb-2 font-medium uppercase tracking-wide" style={{ color: textMuted }}>Preferences</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 rounded-full" style={{ background: accentLight, color: accent }}>{selectedStudent.preferred_role_type}</span>
                    {selectedStudent.open_to_internships && <span className="px-2 py-1 rounded-full" style={{ background: 'rgba(5,150,105,0.1)', color: '#059669' }}>Open to Internships</span>}
                    {selectedStudent.open_to_ppo && <span className="px-2 py-1 rounded-full" style={{ background: 'rgba(5,150,105,0.1)', color: '#059669' }}>Open to PPO</span>}
                    {(selectedStudent.preferred_locations || []).map(l => (
                      <span key={l} className="px-2 py-1 rounded-full" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', color: textSecondary }}>{l}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Resume Preview */}
              <div>
                <p className="text-xs mb-2 font-medium uppercase tracking-wide flex items-center gap-1.5" style={{ color: textMuted }}>
                  <FileText size={12} /> Resume
                </p>
                {resumeLoading ? (
                  <div className="h-40 flex items-center justify-center rounded-xl" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)' }}>
                    <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent, borderTopColor: 'transparent' }} />
                  </div>
                ) : resumeUrl ? (
                  <iframe
                    src={resumeUrl}
                    className="w-full h-80 rounded-xl"
                    style={{ border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)' }}
                    title="Resume Preview"
                  />
                ) : (
                  <div className="h-24 flex items-center justify-center rounded-xl text-sm gap-2" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', border: isDark ? '1px dashed rgba(255,255,255,0.1)' : '1px dashed rgba(0,0,0,0.1)', color: textMuted }}>
                    <FileText size={16} /> No resume uploaded
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="pt-2" style={{ borderTop: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)' }}>
                {candidateActions[selectedStudent.user_id] ? (
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', color: textSecondary, border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)' }}>
                        {candidateActions[selectedStudent.user_id].status} ✓
                      </span>
                      {candidateActions[selectedStudent.user_id].actionedAt && (
                        <p className="text-xs mt-1 ml-1" style={{ color: textMuted }}>
                          {new Date(candidateActions[selectedStudent.user_id].actionedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7 px-3 shrink-0"
                      style={{ borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', background: 'transparent', color: textSecondary }}
                      onClick={() => { handleReconsider(selectedStudent.user_id); handleCloseModal(); }}
                      disabled={actionLoading === selectedStudent.user_id}
                    >
                      Reconsider
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {/* Interested = GREEN */}
                    <Button
                      className="gap-1.5 font-semibold"
                      style={{ background: 'rgba(5,150,105,0.15)', color: '#059669', border: '1px solid rgba(5,150,105,0.3)' }}
                      onClick={() => { handleAction(selectedStudent.user_id, "Interested"); handleCloseModal(); }}
                    >
                      <ThumbsUp size={14} /> Interested
                    </Button>
                    {/* Interview Scheduled = BLUE */}
                    <Button
                      className="gap-1.5 font-semibold"
                      style={{ background: 'rgba(59,130,246,0.15)', color: '#2563eb', border: '1px solid rgba(59,130,246,0.3)' }}
                      onClick={() => { handleAction(selectedStudent.user_id, "Interview Scheduled"); handleCloseModal(); }}
                    >
                      <CalendarCheck size={14} /> Interview
                    </Button>
                    {/* Pass/Rejected = RED */}
                    <Button
                      className="gap-1.5 font-semibold"
                      style={{ background: 'rgba(239,68,68,0.15)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.3)' }}
                      onClick={() => { handleAction(selectedStudent.user_id, "Rejected"); handleCloseModal(); }}
                    >
                      <ThumbsDown size={14} /> Pass
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
